import { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  fetchHistoricoComprasLojaFisica,
  fetchPedidoHistoricoLojaFisica,
  aprovarPedidoHistoricoLojaFisica,
  rejeitarPedidoHistoricoLojaFisica,
  atualizarItensPedidoHistoricoLojaFisica,
  fetchProdutoDetalhe,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { gerarPDFLojaFisica } from '../../components/ListaComprasLojaFisicaPanel/ListaComprasLojaFisicaPanel';
import { useResizableModal } from '../../hooks/useResizableModal';
import ResizableHandles from '../../components/ResizableHandles/ResizableHandles';
import styles from './AprovacoesComprasLojaFisica.module.css';

const formatarMoeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatarData = (dt) =>
  new Date(dt).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const STATUS_CONFIG = {
  aguardando_aprovacao: { label: 'Aguardando', cor: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.25)', icon: '⏳' },
  aprovado:            { label: 'Aprovado',    cor: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.25)',  icon: '✅' },
  rejeitado:           { label: 'Rejeitado',   cor: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.25)',  icon: '❌' },
};

const LOJA_CONFIG = {
  '001': { label: 'Centro', icon: '🏢', cor: '#6366f1', bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.3)' },
  '004': { label: 'Caxias', icon: '🏪', cor: '#f97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)' },
};

const LojaBadge = ({ loja }) => {
  const cfg = LOJA_CONFIG[loja];
  if (!cfg) return null;
  return (
    <span style={{
      background: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.border}`,
      borderRadius: '12px', padding: '1px 10px', fontSize: '0.73rem', fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// ── Chip de status ────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.aguardando_aprovacao;
  return (
    <span className={styles.chip} style={{ color: cfg.cor, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// ── Tooltip de imagem + histórico de produto ──────────────────────────────────
// Calcula posição com clamp para não sair da viewport.
const TOOLTIP_W = 230;
const TOOLTIP_H = 400; // imagem + nome + mini-tabela de 6 meses

const calcTooltipPos = (clientX, clientY) => {
  const x = clientX + 16 + TOOLTIP_W > window.innerWidth
    ? clientX - TOOLTIP_W - 16
    : clientX + 16;
  const y = clientY + TOOLTIP_H > window.innerHeight
    ? Math.max(8, window.innerHeight - TOOLTIP_H - 8)
    : clientY;
  return { x, y };
};

const MESES_ABREV = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesParaNumero = (mes) => {
  if (typeof mes === 'number') return mes;
  const idx = MESES_ABREV.indexOf(String(mes).toLowerCase().slice(0, 3));
  return idx >= 0 ? idx + 1 : (parseInt(mes, 10) || 0);
};
const rotuloMes = (mes, ano) => {
  const n = mesParaNumero(mes);
  const nome = MESES_ABREV[n - 1] || '?';
  return `${nome}/${String(ano).slice(-2)}`;
};

// Cache em memória do histórico por código (evita refetch ao re-hoverar).
// Valor: { status: 'loading'|'ok'|'erro', historico?: [] }
const historicoProdutoCache = new Map();

// Card flutuante com a foto do produto. Estado próprio (resetado por produto via key).
// Fonte: proxy local /api/produtos/imagem/:codigo (NUNCA S3 do Bling — URL presigned expira → 403).
// Fallback: URL externa http não-S3 (loja física / Go2). Se tudo falhar, placeholder "Sem imagem".
const TooltipImagemProduto = ({ codigo, nome, produto, x, y }) => {
  const urlExterna = produto?.imagemBase64 || produto?.imagem_url || produto?.imagemUrl;
  const ehString = typeof urlExterna === 'string';
  const urlExternaFallback = ehString && urlExterna.startsWith('http') && !urlExterna.includes('amazonaws.com')
    ? urlExterna
    : null;
  const srcInicial = ehString && urlExterna.startsWith('data:')
    ? urlExterna
    : `/api/produtos/imagem/${encodeURIComponent(codigo)}`;

  const [src, setSrc] = useState(srcInicial);
  const [falhou, setFalhou] = useState(false);
  const tentouFallback = useRef(false);

  const handleError = () => {
    if (!tentouFallback.current && urlExternaFallback && src !== urlExternaFallback) {
      tentouFallback.current = true;
      setSrc(urlExternaFallback);
    } else {
      setFalhou(true);
    }
  };

  // ── Histórico de 6 meses (compras/entradas + vendas) ─────────────────────────
  // Cache guarda só resultados resolvidos ('ok'/'erro'); mid-flight re-hover refaz
  // o fetch (barato, cache de 600s no servidor) em vez de travar em "carregando".
  const [hist, setHist] = useState(() => historicoProdutoCache.get(codigo) || null);

  useEffect(() => {
    const cached = historicoProdutoCache.get(codigo);
    if (cached) { setHist(cached); return; }

    let vivo = true;
    setHist({ status: 'loading' });
    // Pequeno atraso: evita disparar fetch ao varrer linhas rapidamente.
    const timer = setTimeout(async () => {
      try {
        const r = await fetchProdutoDetalhe('loja-fisica', codigo);
        const lista = (r && r.success && Array.isArray(r.data?.historicoVendas))
          ? r.data.historicoVendas
              .map(h => ({
                mes: mesParaNumero(h.mes),
                ano: Number(h.ano),
                vendas: Number(h.quantidade || 0),
                compras: Number(h.qtdEntrada || 0),
              }))
              .sort((a, b) => (a.ano - b.ano) || (a.mes - b.mes))
          : [];
        const valor = { status: 'ok', historico: lista };
        historicoProdutoCache.set(codigo, valor);
        if (vivo) setHist(valor);
      } catch {
        const valor = { status: 'erro', historico: [] };
        historicoProdutoCache.set(codigo, valor);
        if (vivo) setHist(valor);
      }
    }, 220);

    return () => { vivo = false; clearTimeout(timer); };
  }, [codigo]);

  return (
    <div
      style={{
        position: 'fixed', left: x, top: y, zIndex: 9999,
        background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 6,
        pointerEvents: 'none', width: TOOLTIP_W,
      }}
    >
      {falhou ? (
        <div style={{ height: 168, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.7rem', background: '#f9fafb', borderRadius: 4 }}>
          Sem imagem
        </div>
      ) : (
        <img
          src={src}
          alt={codigo}
          style={{ width: '100%', height: 168, objectFit: 'contain', background: '#f9fafb', borderRadius: 4 }}
          onError={handleError}
        />
      )}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#111827', marginTop: 4 }}>{codigo}</div>
      <div style={{ fontSize: '0.68rem', color: '#6b7280', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{nome}</div>

      {/* Histórico de 6 meses — compras (entradas) e vendas, total das lojas */}
      <div style={{ marginTop: 6, borderTop: '1px solid #eef0f3', paddingTop: 5 }}>
        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', marginBottom: 3 }}>
          ÚLTIMOS 6 MESES · TOTAL LOJAS
        </div>
        {(!hist || hist.status === 'loading') && (
          <div style={{ fontSize: '0.64rem', color: '#9ca3af' }}>Carregando histórico…</div>
        )}
        {hist && hist.status === 'erro' && (
          <div style={{ fontSize: '0.64rem', color: '#9ca3af' }}>Histórico indisponível</div>
        )}
        {hist && hist.status === 'ok' && hist.historico.length === 0 && (
          <div style={{ fontSize: '0.64rem', color: '#9ca3af' }}>Sem movimentação no período</div>
        )}
        {hist && hist.status === 'ok' && hist.historico.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.64rem' }}>
            <thead>
              <tr style={{ color: '#94a3b8' }}>
                <th style={{ textAlign: 'left', fontWeight: 600, padding: '1px 0' }}>Mês</th>
                <th style={{ textAlign: 'right', fontWeight: 600, padding: '1px 0', color: '#2563eb' }}>Compras</th>
                <th style={{ textAlign: 'right', fontWeight: 600, padding: '1px 0', color: '#16a34a' }}>Vendas</th>
              </tr>
            </thead>
            <tbody>
              {hist.historico.map((h) => (
                <tr key={`${h.ano}-${h.mes}`}>
                  <td style={{ textAlign: 'left', color: '#475569', padding: '1px 0' }}>{rotuloMes(h.mes, h.ano)}</td>
                  <td style={{ textAlign: 'right', fontWeight: h.compras > 0 ? 700 : 400, color: h.compras > 0 ? '#2563eb' : '#cbd5e1', padding: '1px 0' }}>{h.compras}</td>
                  <td style={{ textAlign: 'right', fontWeight: h.vendas > 0 ? 700 : 400, color: h.vendas > 0 ? '#16a34a' : '#cbd5e1', padding: '1px 0' }}>{h.vendas}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── Modal ─────────────────────────────────────────────────────────────────────
const ModalAprovacao = ({ pedidoId, onClose, onAtualizar, isAdmin }) => {
  const [pedido, setPedido] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [obs, setObs] = useState('');
  const [processando, setProcessando] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [itensEditados, setItensEditados] = useState([]);
  const [salvandoItens, setSalvandoItens] = useState(false);
  const [hoverImg, setHoverImg] = useState(null); // { codigo, nome, x, y }
  const [codigoCopiado, setCodigoCopiado] = useState(null);

  // Resize estilo Windows (hook compartilhado entre modais)
  const { modalRef, modalStyle, resizing, iniciarResize } = useResizableModal({
    storageKey: 'aprovacaoLojaFisica',
  });

  // Copia o código do produto (Ctrl+C automático ao clicar). Fallback p/ navegadores sem clipboard API.
  const copiarCodigo = async (cod) => {
    if (!cod || cod === '-') return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(cod);
      } else {
        const ta = document.createElement('textarea');
        ta.value = cod;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCodigoCopiado(cod);
      setTimeout(() => setCodigoCopiado(c => (c === cod ? null : c)), 1200);
    } catch {
      /* silencioso — se falhar, não trava a UI */
    }
  };

  useEffect(() => {
    fetchPedidoHistoricoLojaFisica(pedidoId).then(r => {
      if (r.success) {
        setPedido(r.data);
        setItensEditados(r.data.itens || []);
      }
      setCarregando(false);
    });
  }, [pedidoId]);

  const handleAprovar = async () => {
    setProcessando('aprovar');
    const r = await aprovarPedidoHistoricoLojaFisica(pedidoId, obs);
    if (r.success) { onAtualizar(); onClose(); }
    else alert('Erro ao aprovar: ' + r.message);
    setProcessando(null);
  };

  const handleRejeitar = async () => {
    if (!obs.trim()) { alert('Por favor, informe o motivo da rejeição.'); return; }
    setProcessando('rejeitar');
    const r = await rejeitarPedidoHistoricoLojaFisica(pedidoId, obs);
    if (r.success) { onAtualizar(); onClose(); }
    else alert('Erro ao rejeitar: ' + r.message);
    setProcessando(null);
  };

  const handleGerarPDF = async () => {
    setProcessando('pdf');
    try { await gerarPDFLojaFisica(pedido.itens); }
    finally { setProcessando(null); }
  };

  // ── Edição de itens ──────────────────────────────────────────────────────
  const alterarQuantidade = (idx, delta) => {
    setItensEditados(prev => {
      const novo = [...prev];
      const novaQtd = Math.max(1, (novo[idx].quantidade || 1) + delta);
      novo[idx] = { ...novo[idx], quantidade: novaQtd };
      return novo;
    });
  };

  const definirQuantidade = (idx, valor) => {
    const qtd = Math.max(1, parseInt(valor) || 1);
    setItensEditados(prev => {
      const novo = [...prev];
      novo[idx] = { ...novo[idx], quantidade: qtd };
      return novo;
    });
  };

  const removerItem = (idx) => {
    if (itensEditados.length <= 1) { alert('O pedido deve ter ao menos 1 produto.'); return; }
    setItensEditados(prev => prev.filter((_, i) => i !== idx));
  };

  const salvarEdicao = async () => {
    setSalvandoItens(true);
    const r = await atualizarItensPedidoHistoricoLojaFisica(pedidoId, itensEditados);
    if (r.success) {
      setPedido(prev => ({
        ...prev,
        itens: itensEditados,
        total_itens: itensEditados.length,
        valor_total: itensEditados.reduce((acc, i) =>
          acc + (i.produto?.precoCusto || 0) * i.quantidade * 12, 0),
      }));
      setModoEdicao(false);
    } else {
      alert('Erro ao salvar: ' + r.message);
    }
    setSalvandoItens(false);
  };

  const cancelarEdicao = () => {
    setItensEditados(pedido.itens || []);
    setModoEdicao(false);
  };

  if (carregando) return (
    <div className={styles.backdrop}>
      <div className={styles.modal}>
        <div className={styles.loadingCenter}><Spinner animation="border" variant="primary" /></div>
      </div>
    </div>
  );
  if (!pedido) return null;

  const cfg = STATUS_CONFIG[pedido.status] || STATUS_CONFIG.aguardando_aprovacao;
  const itensAtivos = modoEdicao ? itensEditados : (pedido.itens || []);

  const grupos = {};
  itensAtivos.forEach(({ produto, quantidade }, idx) => {
    const forn = produto?.fornecedor || 'Sem Fornecedor';
    if (!grupos[forn]) grupos[forn] = [];
    grupos[forn].push({ produto, quantidade, idx });
  });
  const fornecedores = Object.keys(grupos).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const totalEditado = itensEditados.reduce(
    (acc, i) => acc + (i.produto?.precoCusto || 0) * i.quantidade * 12, 0
  );

  return (
    <div className={styles.backdrop} onClick={modoEdicao ? undefined : onClose}>
      <div
        ref={modalRef}
        className={`${styles.modal} ${resizing ? styles.modalResizing : ''}`}
        style={modalStyle}
        onClick={e => e.stopPropagation()}
      >
        <ResizableHandles onResize={iniciarResize} />

        {/* Header */}
        <div className={styles.modalHead} style={{ borderTop: `3px solid ${cfg.cor}` }}>
          <div className={styles.modalHeadLeft}>
            <div className={styles.modalHeadIcon} style={{ background: cfg.bg, color: cfg.cor }}>
              {cfg.icon}
            </div>
            <div>
              <div className={styles.modalTitulo}>{pedido.titulo}</div>
              <div className={styles.modalMeta}>
                <span>📅 {formatarData(pedido.data_criacao)}</span>
                {pedido.usuario_nome && <span>· 👤 {pedido.usuario_nome}</span>}
                <StatusChip status={pedido.status} />
                <LojaBadge loja={pedido.loja_origem} />
              </div>
              {pedido.status !== 'aguardando_aprovacao' && pedido.aprovado_por && (
                <div className={styles.modalAcao} style={{ color: cfg.cor }}>
                  {pedido.status === 'aprovado' ? '✅ Aprovado' : '❌ Rejeitado'} por{' '}
                  <strong>{pedido.aprovado_por}</strong> · {formatarData(pedido.data_aprovacao)}
                  {pedido.observacao_admin && <span className={styles.obsTexto}> — "{pedido.observacao_admin}"</span>}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAdmin && pedido.status === 'aguardando_aprovacao' && !modoEdicao && (
              <button
                className={styles.btnEditar}
                onClick={() => setModoEdicao(true)}
                title="Editar quantidades ou remover produtos"
              >
                ✏️ Editar Itens
              </button>
            )}
            {!modoEdicao && pedido.status === 'aguardando_aprovacao' && (
              <button
                className={styles.btnPDFHeader}
                onClick={handleGerarPDF}
                disabled={!!processando}
                title="Gerar PDF do pedido sem aprovar"
              >
                {processando === 'pdf' ? <Spinner size="sm" animation="border" /> : '📄 Gerar PDF'}
              </button>
            )}
            <button className={styles.modalCloseBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Stats rápidos */}
        <div className={styles.modalStats}>
          <div className={styles.modalStat}>
            <div className={styles.modalStatNum}>{modoEdicao ? itensEditados.length : pedido.total_itens}</div>
            <div className={styles.modalStatLabel}>Produtos</div>
          </div>
          <div className={styles.modalStat}>
            <div className={styles.modalStatNum} style={{ color: modoEdicao ? '#f59e0b' : '#60a5fa' }}>
              {formatarMoeda(modoEdicao ? totalEditado : pedido.valor_total)}
            </div>
            <div className={styles.modalStatLabel}>{modoEdicao ? 'Novo Total' : 'Custo estimado'}</div>
          </div>
          <div className={styles.modalStat}>
            <div className={styles.modalStatNum}>{fornecedores.length}</div>
            <div className={styles.modalStatLabel}>Fornecedores</div>
          </div>
        </div>

        {/* Banner modo edição */}
        {modoEdicao && (
          <div className={styles.editBanner}>
            ✏️ <strong>Modo Edição</strong> — Ajuste as quantidades ou remova produtos antes de aprovar.
          </div>
        )}

        {/* Itens */}
        <div className={styles.modalBody}>
          {fornecedores.map(forn => {
            const itensGrupo = grupos[forn];
            const subtotal = itensGrupo.reduce(
              (acc, i) => acc + (i.produto?.precoCusto || 0) * i.quantidade * 12, 0
            );
            return (
              <div key={forn} className={styles.grupo}>
                <div className={styles.grupoHead}>
                  <span className={styles.grupoNome}>{forn}</span>
                  <span className={styles.grupoSub}>{formatarMoeda(subtotal)}</span>
                </div>
                <table className={styles.tabela}>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descrição</th>
                      <th>Dz.</th>
                      <th>Vlr. Unit.</th>
                      <th>Subtotal</th>
                      {modoEdicao && <th style={{ width: 90, textAlign: 'center' }}>Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {itensGrupo.map(({ produto, quantidade, idx }) => {
                      const cod = produto?.codigo || produto?.codigoInterno || '-';
                      const nome = produto?.nome || produto?.descricao || '-';
                      const preco = produto?.precoCusto || 0;
                      return (
                        <tr
                          key={cod}
                          style={modoEdicao ? { background: 'rgba(245,158,11,0.04)' } : {}}
                          onMouseEnter={(e) => {
                            const pos = calcTooltipPos(e.clientX, e.clientY);
                            setHoverImg({ codigo: cod, nome, produto, x: pos.x, y: pos.y });
                          }}
                          onMouseMove={(e) => {
                            if (!hoverImg) return;
                            const pos = calcTooltipPos(e.clientX, e.clientY);
                            setHoverImg(prev => prev ? { ...prev, x: pos.x, y: pos.y } : prev);
                          }}
                          onMouseLeave={() => setHoverImg(null)}
                        >
                          <td
                            className={styles.codCell}
                            onClick={(e) => { e.stopPropagation(); copiarCodigo(cod); }}
                            title="Clique para copiar o código"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            {codigoCopiado === cod
                              ? <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ copiado</span>
                              : cod}
                          </td>
                          <td className={styles.nomeCell}>{nome}</td>
                          <td className={styles.centro}>
                            {modoEdicao ? (
                              <div className={styles.qtdControls}>
                                <button className={styles.qtdBtn} onClick={() => alterarQuantidade(idx, -1)}>−</button>
                                <input
                                  type="number"
                                  className={styles.qtdInput}
                                  value={quantidade}
                                  min={1}
                                  onChange={e => definirQuantidade(idx, e.target.value)}
                                />
                                <button className={styles.qtdBtn} onClick={() => alterarQuantidade(idx, +1)}>+</button>
                              </div>
                            ) : (
                              <strong>{quantidade}</strong>
                            )}
                          </td>
                          <td className={styles.direita}>{preco > 0 ? formatarMoeda(preco) : '—'}</td>
                          <td className={styles.direita + ' ' + styles.subtotalCell}>
                            {preco > 0 ? formatarMoeda(preco * quantidade * 12) : '—'}
                          </td>
                          {modoEdicao && (
                            <td style={{ textAlign: 'center' }}>
                              <button
                                className={styles.btnRemoverItem}
                                onClick={() => removerItem(idx)}
                                title="Remover produto"
                              >
                                🗑️
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className={styles.modalFoot}>
          {modoEdicao && (
            <div className={styles.footBtns} style={{ marginBottom: 12 }}>
              <button className={styles.btnCancelarEd} onClick={cancelarEdicao} disabled={salvandoItens}>
                ✕ Cancelar
              </button>
              <button className={styles.btnSalvarEd} onClick={salvarEdicao} disabled={salvandoItens}>
                {salvandoItens ? <><Spinner size="sm" animation="border" /> Salvando...</> : '💾 Salvar Alterações'}
              </button>
            </div>
          )}

          {isAdmin && pedido.status === 'aguardando_aprovacao' && !modoEdicao && (
            <>
              <textarea
                className={styles.obsInput}
                placeholder="Observação (obrigatória para rejeição, opcional para aprovação)..."
                value={obs}
                onChange={e => setObs(e.target.value)}
                rows={2}
              />
              <div className={styles.footBtns}>
                <button className={styles.btnRej} onClick={handleRejeitar} disabled={!!processando}>
                  {processando === 'rejeitar' ? <Spinner size="sm" animation="border" /> : '❌ Rejeitar'}
                </button>
                <button className={styles.btnAprov} onClick={handleAprovar} disabled={!!processando}>
                  {processando === 'aprovar' ? <Spinner size="sm" animation="border" /> : '✅ Aprovar Pedido'}
                </button>
              </div>
            </>
          )}

          {pedido.status === 'aprovado' && (
            <button className={styles.btnPDF} onClick={handleGerarPDF} disabled={processando === 'pdf'}>
              {processando === 'pdf'
                ? <><Spinner size="sm" animation="border" className="me-2" /> Gerando...</>
                : '📄 Gerar PDF da Lista de Compras'}
            </button>
          )}

          {pedido.status === 'rejeitado' && (
            <div className={styles.rejMsg}>❌ Pedido rejeitado — PDF não disponível.</div>
          )}

          {!isAdmin && pedido.status === 'aguardando_aprovacao' && (
            <div className={styles.aguardMsg}>⏳ Aguardando aprovação do administrador.</div>
          )}
        </div>

      </div>

      {/* Tooltip flutuante de imagem do produto */}
      {hoverImg && (
        <TooltipImagemProduto
          key={hoverImg.codigo}
          codigo={hoverImg.codigo}
          nome={hoverImg.nome}
          produto={hoverImg.produto}
          x={hoverImg.x}
          y={hoverImg.y}
        />
      )}
    </div>
  );
};

// ── Página ────────────────────────────────────────────────────────────────────
const AprovacoesComprasLojaFisica = () => {
  const { isAdmin } = useAuth();
  const [todos, setTodos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('aguardando_aprovacao');
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const r = await fetchHistoricoComprasLojaFisica({ limit: 200 });
    if (r.success) setTodos(r.data);
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const contarStatus = (s) => todos.filter(p => p.status === s).length;
  const pedidos = filtro === 'todos' ? todos : todos.filter(p => p.status === filtro);

  const contadores = [
    { key: 'aguardando_aprovacao', label: 'Aguardando',  cor: '#f59e0b', corBg: 'rgba(245,158,11,0.1)', icon: '⏳' },
    { key: 'aprovado',             label: 'Aprovados',   cor: '#3b82f6', corBg: 'rgba(59,130,246,0.1)',  icon: '✅' },
    { key: 'rejeitado',            label: 'Rejeitados',  cor: '#ef4444', corBg: 'rgba(239,68,68,0.1)',  icon: '❌' },
  ];

  return (
    <div className={styles.page}>
      {/* Hero Header */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <div className={styles.heroIcon}>🏪</div>
            <div>
              <h1 className={styles.heroTitle}>Aprovações — Loja Física</h1>
              <p className={styles.heroSub}>
                {isAdmin()
                  ? 'Analise e aprove os pedidos de compra da loja física para liberar o PDF.'
                  : 'Acompanhe o status dos seus pedidos da loja física enviados para aprovação.'}
              </p>
            </div>
          </div>
          <div className={styles.heroRight}>
            <div className={styles.totalBadge}>
              <div className={styles.totalNum}>{todos.length}</div>
              <div className={styles.totalLabel}>Total de Pedidos</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.body}>
        {/* Contadores filtro */}
        <div className={styles.filtros}>
          {contadores.map(c => (
            <button
              key={c.key}
              className={`${styles.filtroCard} ${filtro === c.key ? styles.filtroAtivo : ''}`}
              style={filtro === c.key ? { borderColor: c.cor, background: c.corBg } : {}}
              onClick={() => setFiltro(c.key === filtro ? 'todos' : c.key)}
            >
              <div className={styles.filtroIcon} style={{ color: c.cor }}>{c.icon}</div>
              <div className={styles.filtroNum} style={{ color: filtro === c.key ? c.cor : '#e2e8f0' }}>
                {contarStatus(c.key)}
              </div>
              <div className={styles.filtroLabel}>{c.label}</div>
            </button>
          ))}
        </div>

        {/* Lista */}
        {carregando ? (
          <div className={styles.loadingWrap}>
            <div className={styles.loadingSpinner}>
              <Spinner animation="border" variant="primary" />
            </div>
            <span>Carregando pedidos...</span>
          </div>
        ) : pedidos.length === 0 ? (
          <div className={styles.vazio}>
            <div className={styles.vazioCircle}>
              {filtro === 'aguardando_aprovacao' ? '📭' : filtro === 'aprovado' ? '✅' : '❌'}
            </div>
            <div className={styles.vazioTitulo}>Nenhum pedido encontrado</div>
            <div className={styles.vazioSub}>
              {filtro === 'aguardando_aprovacao'
                ? 'Quando um usuário enviar uma lista de compras da loja física, ela aparecerá aqui para análise.'
                : 'Nenhum pedido com este status no momento.'}
            </div>
          </div>
        ) : (
          <div className={styles.lista}>
            {pedidos.map(pedido => {
              const cfg = STATUS_CONFIG[pedido.status] || STATUS_CONFIG.aguardando_aprovacao;
              return (
                <div
                  key={pedido.id}
                  className={styles.card}
                  onClick={() => setDetalhe(pedido.id)}
                >
                  <div className={styles.cardStripe} style={{ background: cfg.cor }} />
                  <div className={styles.cardIcon} style={{ background: cfg.bg, color: cfg.cor }}>
                    {cfg.icon}
                  </div>
                  <div className={styles.cardMain}>
                    <div className={styles.cardTitulo}>{pedido.titulo}</div>
                    <div className={styles.cardMeta}>
                      <span className={styles.metaItem}>📅 {formatarData(pedido.data_criacao)}</span>
                      {pedido.usuario_nome && (
                        <span className={styles.metaItem}>👤 {pedido.usuario_nome}</span>
                      )}
                      <LojaBadge loja={pedido.loja_origem} />
                    </div>
                    {pedido.observacao_admin && (
                      <div className={styles.cardObs}>💬 {pedido.observacao_admin}</div>
                    )}
                  </div>
                  <div className={styles.cardRight}>
                    <StatusChip status={pedido.status} />
                    <div className={styles.cardQtd}>{pedido.total_itens} produto{pedido.total_itens !== 1 ? 's' : ''}</div>
                    {pedido.valor_total > 0 && (
                      <div className={styles.cardValor}>{formatarMoeda(pedido.valor_total)}</div>
                    )}
                    <div className={styles.cardSeta}>→</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {detalhe && (
        <ModalAprovacao
          pedidoId={detalhe}
          onClose={() => setDetalhe(null)}
          onAtualizar={carregar}
          isAdmin={isAdmin()}
        />
      )}
    </div>
  );
};

export default AprovacoesComprasLojaFisica;
